import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, Clock, Sparkles, Video, PlayCircle, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import api from '../lib/api';

// Optimize Cloudinary URLs
const opt = (url, w) => {
  if (!url) return url;
  if (url.includes('res.cloudinary.com') && !url.includes('f_auto')) {
    return url.replace('/upload/', `/upload/f_auto,q_auto${w ? `,w_${w}` : ''}/`);
  }
  return url;
};

// Extract Drive / YouTube id from an arbitrary URL (or assume it's already an id).
// Returns { source: 'drive'|'youtube'|null, id: string|null }.
const parseVideoUrl = (url) => {
  if (!url) return { source: null, id: null };
  try {
    // Drive: https://drive.google.com/file/d/{ID}/view  OR  /preview  OR ?id={ID}
    const driveMatch = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([a-zA-Z0-9_-]{20,})/);
    if (driveMatch) return { source: 'drive', id: driveMatch[1] };
    // YouTube
    const ytWatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/);
    if (ytWatch) return { source: 'youtube', id: ytWatch[1] };
    // If it looks like a raw Drive ID (no slashes, long alphanumeric)
    if (/^[a-zA-Z0-9_-]{20,}$/.test(url)) return { source: 'drive', id: url };
  } catch { /* noop */ }
  return { source: null, id: null };
};

// Embedded video modal — locks scroll, uses Drive /preview or YouTube nocookie embed.
// `/preview` removes the visible "download" button that Drive shows on `/view`.
// Note: cannot prevent screen-recording or determined exfiltration, but covers the
// 95 % of casual share/download scenarios.
const VideoModal = ({ url, title, onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const { source, id } = parseVideoUrl(url);
  let embed = null;
  if (source === 'drive' && id) {
    embed = `https://drive.google.com/file/d/${id}/preview`;
  } else if (source === 'youtube' && id) {
    embed = `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&showinfo=0`;
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="masterclass-recording-modal"
    >
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute -top-10 sm:-top-12 right-0 inline-flex items-center gap-1.5 text-white/85 hover:text-white text-sm transition-colors"
          data-testid="masterclass-recording-close"
        >
          <X className="w-5 h-5" /> Cerrar
        </button>
        <div className="relative w-full bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10" style={{ paddingBottom: '56.25%' }}>
          {embed ? (
            <iframe
              src={embed}
              title={title || 'Grabación de la masterclass'}
              className="absolute inset-0 w-full h-full"
              width="100%"
              height="100%"
              frameBorder="0"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture; accelerometer; gyroscope"
              allowFullScreen
              loading="eager"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              Formato de vídeo no soportado.
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-white/55">
          <span className="text-center sm:text-left">
            Grabación disponible durante 48 h · Por respeto al espacio compartido, te pedimos no descargar ni redistribuir el contenido.
          </span>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-white/75 hover:text-white underline-offset-2 hover:underline whitespace-nowrap"
              data-testid="masterclass-recording-fullscreen"
            >
              ¿Problemas para verlo? Abrir en pantalla completa
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const fmtDateLong = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long',
      timeZone: 'Europe/Madrid',
    });
  } catch { return ''; }
};
const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
    });
  } catch { return ''; }
};

/**
 * MasterclassCard
 * Visible inside /mi-camino when:
 *  - masterclass is published (config.is_published)
 *  - shown to ALL logged users (enrolled or not). If enrolled → full info. If not → invitation.
 */
export default function MasterclassCard({ data, onEnrolled }) {
  const [busy, setBusy] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  if (!data || !data.config || !data.config.is_published) return null;

  // Mobile: bypass the embedded modal — Drive's /preview iframe renders
  // poorly on mobile browsers (translucent black overlay, broken controls).
  // Instead, open Drive's mobile-optimized viewer in a new tab where playback
  // works correctly. Download stays disabled at the Drive file-permission level.
  const openRecording = () => {
    const url = data.config?.recording_url;
    if (!url) return;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setShowRecording(true);
    }
  };

  const { config, is_enrolled, recording_available_now } = data;
  const date = fmtDateLong(config.date_iso);
  const start = fmtTime(config.date_iso);
  const endDate = new Date(config.date_iso);
  endDate.setMinutes(endDate.getMinutes() + (config.duration_minutes || 90));
  const end = fmtTime(endDate.toISOString());

  const enroll = async () => {
    setBusy(true);
    try {
      const r = await api.post('/masterclass/enroll');
      toast.success(r.data.already_enrolled ? 'Ya estabas inscrita' : '¡Plaza reservada! Te esperamos.');
      onEnrolled?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo completar la inscripción');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#c9a227]/30 mb-6 bg-[#1a2332]"
      data-testid="mi-camino-masterclass-card"
    >
      {/* Poster image (no overlay text — the poster has its own typography) */}
      <img
        src={opt(config.cover_image_url, 1200)}
        alt={config.title}
        className="w-full h-auto block"
      />
      {/* CTA band below the poster */}
      <div className="bg-gradient-to-b from-[#1a2332] to-[#2d3748] p-6 sm:p-7">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#c9a227]/20 backdrop-blur-sm text-[#e8c75a] rounded-full text-[11px] font-medium uppercase tracking-wider mb-3">
          <Sparkles className="w-3 h-3" /> Masterclass gratuita
        </span>
        <div className="flex flex-wrap gap-2 mb-5">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/85 rounded-full text-xs">
            <Calendar className="w-3 h-3" /> {date}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/85 rounded-full text-xs">
            <Clock className="w-3 h-3" /> {start}–{end}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/85 rounded-full text-xs">
            <Video className="w-3 h-3" /> Online en directo
          </span>
        </div>

        {is_enrolled ? (
          <div className="flex flex-wrap gap-3">
            {config.meet_url ? (
              <a
                href={config.meet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#c9a227] hover:bg-[#b89220] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                data-testid="masterclass-join-meet"
              >
                <Video className="w-4 h-4" /> Unirme a Google Meet
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-5 py-2.5 rounded-full text-sm">
                <Clock className="w-4 h-4" /> Enlace de Meet próximamente
              </span>
            )}
            {recording_available_now && config.recording_url && (
              <button
                type="button"
                onClick={openRecording}
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                data-testid="masterclass-watch-recording"
              >
                <PlayCircle className="w-4 h-4" /> Ver grabación (48 h)
              </button>
            )}
          </div>
        ) : (
          <Button
            onClick={enroll}
            disabled={busy}
            className="bg-[#c9a227] hover:bg-[#b89220] text-white px-5 py-2.5 rounded-full text-sm font-medium"
            data-testid="masterclass-card-enroll"
          >
            {busy ? 'Reservando…' : 'Inscribirme gratuitamente'}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
      {showRecording && (
        <VideoModal
          url={config.recording_url}
          title={config.title}
          onClose={() => setShowRecording(false)}
        />
      )}
    </div>
  );
}

/**
 * LimitesUpsellCard
 * Visible to: all logged users who don't have a LIMITES special_enrollment.
 * Hidden when prop `hasLimites` is true.
 */
export function LimitesUpsellCard({ hasLimites, ctaUrl = '/formaciones/limites' }) {
  if (hasLimites) return null;
  return (
    <Link to={ctaUrl} className="block mb-6 group" data-testid="mi-camino-limites-upsell">
      <div className="relative overflow-hidden rounded-2xl border border-[#c9a227]/40 p-6 sm:p-7 bg-gradient-to-br from-[#fdfbf5] via-[#faf6ed] to-[#f5f0e6] hover:shadow-lg hover:shadow-[#c9a227]/10 transition-all">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#c9a227]" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-wider text-[#8a7428] font-medium mb-1">Continúa el camino</p>
            <h3 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-1">
              LÍMITES: el espacio original de la consciencia
            </h3>
            <p className="text-sm text-[hsl(var(--text-secondary))] mb-3">
              Si la masterclass ha resonado en ti, este es el master-curso completo de 8 sesiones en directo donde profundizamos en lo que se abrió.
            </p>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#c9a227] group-hover:gap-3 transition-all">
              Activar curso completo <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * Reusable home banner — a discreet gold/cream strip.
 * Reads /api/masterclass/config and renders only when show_homepage_banner is true.
 * Hides on routes that already showcase the masterclass (the masterclass landing itself and /mi-camino).
 */
export function MasterclassHomeBanner() {
  const [cfg, setCfg] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => {
    api.get('/masterclass/config').then(r => setCfg(r.data)).catch(() => {});
  }, []);
  const hiddenOnPath = pathname.startsWith('/masterclass') || pathname.startsWith('/mi-camino');
  const visible = !hiddenOnPath && cfg && cfg.is_published && cfg.show_homepage_banner;
  const date = useMemo(() => cfg ? fmtDateLong(cfg.date_iso) : '', [cfg]);
  const start = useMemo(() => cfg ? fmtTime(cfg.date_iso) : '', [cfg]);
  if (!visible) return null;

  return (
    <div className="w-full bg-gradient-to-r from-[#faf6ed] via-[#f5ecd2] to-[#faf6ed] border-y border-[#e8d9a8] mt-20" data-testid="home-masterclass-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 flex flex-col sm:flex-row items-center gap-3 sm:gap-5 justify-between text-center sm:text-left">
        <div className="flex items-center gap-3 text-[hsl(var(--text-primary))]">
          <Sparkles className="w-4 h-4 text-[#c9a227] flex-shrink-0" />
          <p className="text-xs sm:text-sm">
            <span className="font-medium">Masterclass gratuita · {date} · {start}h</span>
            <span className="text-[hsl(var(--text-secondary))]"> — {cfg.title}</span>
          </p>
        </div>
        <Link
          to={cfg.slug ? `/masterclass/${cfg.slug}` : '/masterclass/primer-limite'}
          className="flex-shrink-0 inline-flex items-center gap-1.5 bg-[#c9a227] hover:bg-[#b89220] text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all"
          data-testid="home-masterclass-banner-cta"
        >
          Reservar mi plaza
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
